import { useCallback, useEffect, useMemo } from 'react';
import type { ColumnOrderState, Updater } from '@tanstack/react-table';
import {
  buildAllLeafColumnIds,
  columnOrdersEqual,
  normalizeColumnOrder,
  resolveSelectableColumnOrder,
} from '../utils/fillerColumnOrder';

type SetColumnOrder = (
  updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)
) => void;

type FillerLockedColumnOrder = {
  effectiveColumnOrder: ColumnOrderState;
  onColumnOrderChange: (updater: Updater<ColumnOrderState>) => void;
};

/**
 * Keeps the trailing Filler gutter column last even when columns are added,
 * removed, or toggled visible (DL-60609).
 */
export const useFillerLockedColumnOrder = (
  columnOrder: ColumnOrderState,
  setColumnOrder: SetColumnOrder,
  columnIds: (string | undefined)[] | undefined,
  isSelectable: boolean
): FillerLockedColumnOrder => {
  const dataColumnIds = useMemo(
    () => columnIds?.filter((id): id is string => Boolean(id)) ?? [],
    [columnIds]
  );

  const allLeafColumnIds = useMemo(
    () => buildAllLeafColumnIds(dataColumnIds, isSelectable),
    [dataColumnIds, isSelectable]
  );

  const effectiveColumnOrder = useMemo((): ColumnOrderState => {
    const base = resolveSelectableColumnOrder(columnOrder, isSelectable, dataColumnIds);
    return normalizeColumnOrder(base, allLeafColumnIds);
  }, [columnOrder, isSelectable, dataColumnIds, allLeafColumnIds]);

  const onColumnOrderChange = useCallback(
    (updater: Updater<ColumnOrderState>) => {
      setColumnOrder(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return normalizeColumnOrder(next, allLeafColumnIds);
      });
    },
    [setColumnOrder, allLeafColumnIds]
  );

  useEffect(() => {
    setColumnOrder(prev => {
      const normalized = normalizeColumnOrder(prev, allLeafColumnIds);
      return columnOrdersEqual(prev, normalized) ? prev : normalized;
    });
  }, [allLeafColumnIds, setColumnOrder]);

  return { effectiveColumnOrder, onColumnOrderChange };
};
